import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  MenuItem,
  Rating,
  Chip,
  Skeleton,
} from '@mui/material';
import api from '../../api';
import { toast } from 'react-toastify';

const ClassFeedbackList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [rating, setRating] = useState('');

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { page: page + 1, per_page: rowsPerPage };
        if (search.trim()) params.search = search.trim();
        if (rating) params.rating = rating;
        const res = await api.get('admin/class-feedback', { params });
        if (cancelled) return;
        const pag = res.data?.data;
        setItems(Array.isArray(pag?.data) ? pag.data : []);
        setTotal(pag?.total ?? 0);
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.message || 'Failed to load class feedback');
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [page, rowsPerPage, search, rating]);

  const when = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? '—'
      : date.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', mb: 0.5 }}>
        Class feedback
      </Typography>
      <Typography variant="body2" sx={{ color: '#5a6c7d', mb: 3 }}>
        Ratings students leave after a live class. Tutors see this on their own classes; this is every class.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          placeholder="Student, class, or comment"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          sx={{ minWidth: 260 }}
        />
        <TextField
          size="small"
          select
          label="Stars"
          value={rating}
          onChange={(e) => { setRating(e.target.value); setPage(0); }}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          {[5, 4, 3, 2, 1].map((n) => (
            <MenuItem key={n} value={String(n)}>{n} star{n === 1 ? '' : 's'}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Class</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tutor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Rating</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Feedback</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Submitted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(6)].map((__, c) => (
                      <TableCell key={c}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No class feedback yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.class_name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[row.course_name, row.subject_name].filter(Boolean).join(' · ') || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.tutor_name || '—'}</TableCell>
                    <TableCell>{row.student_name || '—'}</TableCell>
                    <TableCell>
                      <Rating value={row.rating || 0} readOnly size="small" />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: row.comment ? 0.5 : 0 }}>
                        {(row.preset_labels || []).map((label) => (
                          <Chip key={label} label={label} size="small" variant="outlined" />
                        ))}
                      </Box>
                      {row.comment ? (
                        <Typography variant="body2">“{row.comment}”</Typography>
                      ) : (row.preset_labels || []).length === 0 ? '—' : null}
                    </TableCell>
                    <TableCell>{when(row.submitted_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>
    </Box>
  );
};

export default ClassFeedbackList;
